import React, { useRef, useState, useEffect, Fragment } from "react";
import type { TextProps, ChakraProps, UsePopperProps } from "@chakra-ui/react";
import {
  Menu,
  MenuList,
  MenuItem,
  MenuButton,
  Flex,
  Text,
  useStyleConfig,
  Box
} from "@chakra-ui/react";
import { CheckIcon, ChevronDownIcon } from "components/SvgIcons";
import { colours, shadows } from "@shared/ui.design-system";
type Props = {
  label?: string;
  items: {
    labelPrefixComponent?: React.ReactNode;
    label: string;
    value: unknown;
  }[];
  selected?: string | { label: string; value: unknown };
  placeholder?: string;
  containerStyles?: ChakraProps["css"];
  minWidth?: string | number | Array<number | string>;
  maxWidth?: string | number | Array<number | string>;
  error?: string;
  onChange?: { ({ label, value }): void };
  menuStyles?: ChakraProps["css"];
  testId?: string;
  width?: string;
  backgroundColor?: string;
  showMax10Items?: boolean;
  disabled?: boolean;
  textProps?: TextProps;
  checkIcon?: boolean;
  menuProps?: UsePopperProps;
};
export const Select: React.FC<Props> = ({
  items,
  label,
  placeholder = "",
  selected,
  minWidth,
  maxWidth,
  containerStyles = {},
  onChange,
  width,
  menuStyles,
  disabled = false,
  testId = "Select",
  showMax10Items = false,
  textProps = {},
  backgroundColor,
  checkIcon = false,
  menuProps
}) => {
  const styleSelect = useStyleConfig("Select");
  const styleSelectList = useStyleConfig("SelectList");
  const styleSelectButton = useStyleConfig("SelectButton");
  const styleSelectButtonDisabled = useStyleConfig("SelectButtonDisabled");
  const styleSelectItem = useStyleConfig("SelectItem");

  const refList = useRef<HTMLDivElement | null>(null);
  const refButton = useRef<HTMLButtonElement | null>(null);
  const defaultItem = { label: placeholder, value: null };
  const [selectedItem, setSelectedItem] = useState<{
    labelPrefixComponent?: React.ReactNode;
    label: string;
    value: unknown;
  }>(defaultItem);
  useEffect(() => {
    if (selected) {
      const criteria = typeof selected === "string" ? selected : selected.label;
      const found = items.find((item) => criteria === item.label);
      if (found) {
        if (found !== selectedItem) setSelectedItem(found);
      } else {
        setSelectedItem(defaultItem);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, items, selectedItem]);
  const itemsToMap = showMax10Items ? items.slice(0, 10) : items;

  return (
    <Flex alignItems="center" data-testid={testId} css={containerStyles}>
      {label && (
        <Text
          variant="acuminLight"
          mt={0}
          mr={1}
          fontSize={-4}
          lineHeight="1.1rem"
        >
          {label}
        </Text>
      )}
      <Box sx={styleSelect} css={containerStyles}>
        <Menu autoSelect={false} preventOverflow matchWidth {...menuProps}>
          <MenuButton
            sx={disabled ? styleSelectButtonDisabled : styleSelectButton}
            minWidth={minWidth}
            maxWidth={maxWidth}
            ref={refButton}
            data-testid="SelectButton"
            css={menuStyles}
            disabled={disabled}
            _focus={{
              boxShadow: shadows.bottom.l1
            }}
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Flex alignItems="center">
                {selectedItem?.labelPrefixComponent ?? null}
                <Text
                  data-testid="SelectText"
                  variant="acuminMedium"
                  mt={0}
                  noOfLines={1}
                  textAlign="left"
                  isTruncated
                  whiteSpace="unset"
                  {...textProps}
                >
                  {selectedItem.label}
                </Text>
              </Flex>
              <ChevronDownIcon boxSize="1.25rem" ml="0.5rem" />
            </Flex>
          </MenuButton>
          <MenuList
            ref={refList}
            sx={{ ...styleSelectList, width }}
            data-testid="SelectList"
            css={menuStyles}
          >
            {itemsToMap.map((item, index) => (
              <Fragment key={item.label}>
                <MenuItem
                  className={item === selected ? "selected" : ""}
                  sx={{
                    ...styleSelectItem,
                    background: item === selected ? "inherit !important" : "",
                    _hover: {
                      color: colours.primary.dark,
                      backgroundColor: item === selected ? backgroundColor : ""
                    }
                  }}
                  onClick={() => {
                    setSelectedItem(item);
                    if (onChange) onChange(item);
                  }}
                  data-testid={`SelectItem${index}`}
                  _focus={{
                    boxShadow: shadows.bottom.l1
                  }}
                >
                  <Flex justifyContent="space-between" alignItems="center">
                    <Flex alignItems="center">
                      {item?.labelPrefixComponent ?? null}
                      <Text variant="acuminLight" mt={0}>
                        {item.label}
                      </Text>
                    </Flex>
                    <Box width="16px" height="16px" ml="0.5rem" />
                    {item === selected && checkIcon && (
                      <CheckIcon
                        position="absolute"
                        h="1.5rem"
                        w="1.5rem"
                        right="1rem"
                        color={colours.primary.dark}
                      />
                    )}
                  </Flex>
                </MenuItem>
                {index < items.length - 1 && (
                  <hr style={{ margin: "0 0.8rem" }} />
                )}
              </Fragment>
            ))}
          </MenuList>
        </Menu>
      </Box>
    </Flex>
  );
};
